/**
 * useUpdatePayoutCountry — saves the instructor's country and refreshes
 * everything that reads it.
 *
 * The country is the field payout setup gates on, and two separate caches
 * answer "what country is this instructor in": `user-profile`, which the gate
 * in `EarningsPage` reads, and `instructor-country`, which `usePayoutCurrency`
 * reads to pick the payout minimum quoted on the very next screen. Both are
 * invalidated before this reports success — missing the first leaves the
 * instructor staring at the question they just answered, and missing the second
 * quotes them a threshold in the wrong currency, now for a full `staleTime`
 * rather than a moment.
 *
 * `invalidateQueries` resolves after the refetch, so by the time `onSuccess`
 * runs the gate re-evaluates against the saved value rather than the stale one.
 *
 * Lives in a hook rather than in the component because that is where the
 * architecture notes put repository calls, mutations and cache invalidation —
 * the same reason `useRecordAgreementAcceptance` exists beside it.
 */

import { useMutation, useQueryClient } from '@tanstack/react-query';
import { useAuth } from '@/contexts/AuthContext';
import { walletRepository } from '@/features/wallet/repositories/supabaseWalletRepository';

export interface UseUpdatePayoutCountryOptions {
  /** Runs only after the country is saved and both caches are refreshed. */
  onSuccess: () => void;
}

export function useUpdatePayoutCountry({ onSuccess }: UseUpdatePayoutCountryOptions) {
  const { user } = useAuth();
  const queryClient = useQueryClient();

  const { mutate, isPending, error } = useMutation({
    mutationFn: async (country: string) => {
      if (!user?.id) {
        throw new Error('You must be signed in to set your country.');
      }

      await walletRepository.updatePayoutCountry(user.id, country);

      await Promise.all([
        queryClient.invalidateQueries({ queryKey: ['user-profile', user.id] }),
        queryClient.invalidateQueries({ queryKey: ['instructor-country', user.id] }),
      ]);
    },
    onSuccess,
  });

  return { saveCountry: mutate, isSaving: isPending, saveError: error };
}
